import { pgTable, pgSequence, text, serial, integer, boolean, timestamp, jsonb, bigint, index, uniqueIndex, customType, varchar, check } from "drizzle-orm/pg-core";
import { sql } from "drizzle-orm";
import { createInsertSchema } from "drizzle-zod";
import { z } from "zod";
import { PRODUCT_CONDITIONS } from "./productCondition";

// Custom tsvector type for Full-Text Search
const tsvector = customType<{ data: string }>({
  dataType() { return "tsvector"; },
});

// ─── Shared Zod Validators ────────────────────────────────────────────────────
const normalizePhoneDigits = (value: string): string =>
  value
    .replace(/[٠-٩]/g, (d) => String("٠١٢٣٤٥٦٧٨٩".indexOf(d)))
    .trim();

const iraqiPhoneValidationMessage = "رقم الهاتف غير صحيح. يجب أن يبدأ بـ 07 ويتكون من 11 رقم";

// Iraqi phone: starts with 07, exactly 11 digits (e.g. 07xxxxxxxxx)
export const iraqiPhoneSchema = z
  .string()
  .transform(normalizePhoneDigits)
  .refine((v) => /^07\d{9}$/.test(v), iraqiPhoneValidationMessage);

// Optional Iraqi phone — only validates if a value is provided
export const iraqiPhoneOptionalSchema = z
  .string()
  .transform(normalizePhoneDigits)
  .refine((v) => v === "" || /^07\d{9}$/.test(v), iraqiPhoneValidationMessage)
  .optional()
  .transform((v) => (v == null || v === "" ? undefined : v));

// Account phone — the number a user registers/logs in with. Accepts either the
// Iraqi local format (07XXXXXXXXX, kept for full backward compatibility with
// every existing account) OR an international E.164 number (+<country><national>,
// 8–15 digits). Phone is a contact/uniqueness field; the verified email is the
// real proof of account ownership, so widening this format is safe.
const E164_PHONE = /^\+[1-9]\d{6,14}$/;
const IRAQI_LOCAL_PHONE = /^07\d{9}$/;
export function isUserPhone(value: string): boolean {
  const v = normalizePhoneDigits((value ?? "").trim());
  return IRAQI_LOCAL_PHONE.test(v) || E164_PHONE.test(v);
}
export const userPhoneSchema = z
  .string()
  .transform((v) => normalizePhoneDigits(v.trim()))
  .refine(isUserPhone, "رقم هاتف غير صحيح");

// Positive price — must be an integer greater than zero
export const positivePriceSchema = z
  .number({ invalid_type_error: "السعر يجب أن يكون رقماً" })
  .int("السعر يجب أن يكون رقماً صحيحاً")
  .positive("يجب أن يكون السعر أكبر من الصفر");
// ─────────────────────────────────────────────────────────────────────────────

// Import Auth Models
export * from "./models/auth";
export * from "./adminPermissions";
import { users } from "./models/auth";

// Re-export all non-DB constants from constants.ts
export * from "./constants";

// Listings Table
// Listing types
export const LISTING_TYPES = ["REGULAR", "WHOLESALE"] as const;
export type ListingType = typeof LISTING_TYPES[number];

// Wholesale unit types
export const WHOLESALE_UNITS = ["piece", "dozen", "carton", "pallet"] as const;
export type WholesaleUnit = typeof WHOLESALE_UNITS[number];

// Seller types for wholesale
export const SELLER_TYPES = ["shop", "agent", "importer", "factory", "exporter"] as const;
export type SellerType = typeof SELLER_TYPES[number];

// Tier pricing type
export interface TierPrice {
  minQty: number;
  price: number;
}

export const listings = pgTable("listings", {
  id: serial("id").primaryKey(),
  listingCode: text("listing_code"),
  title: text("title").notNull(),
  description: text("description").notNull(),
  price: bigint("price", { mode: "number" }).notNull(),
  currency: text("currency").default("IQD").notNull(),
  category: text("category").notNull(),
  subcategory: text("subcategory"),
  // «جديد» / «مستعمل» for physical products, NULL where no condition applies
  // (shared/productCondition.ts). No DB default: the condition is never
  // assumed (owner decision 2026-10-19; scripts/migrations/2026-10-product-condition.sql).
  condition: text("condition"),
  location: text("location").notNull(),
  city: text("city"),
  district: text("district"),
  imageUrls: jsonb("image_urls").$type<string[]>().default([]),
  attributes: jsonb("attributes"),
  contactPhone: text("contact_phone"),
  contactWhatsapp: boolean("contact_whatsapp").default(false),
  userId: text("user_id").notNull(),
  isPremium: boolean("is_premium").default(false),
  isFeatured: boolean("is_featured").default(false),
  featuredUntil: timestamp("featured_until"),
  isActive: boolean("is_active").default(true),
  views: integer("views").default(0),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
  
  // Wholesale fields
  listingType: text("listing_type").default("REGULAR"),
  wholesaleUnit: text("wholesale_unit"),
  wholesalePrice: bigint("wholesale_price", { mode: "number" }),
  moq: integer("moq"),
  tierPrices: jsonb("tier_prices").$type<TierPrice[]>(),
  packagingType: text("packaging_type"),
  leadTimeHours: integer("lead_time_hours"),
  sellerType: text("seller_type"),
  warehouseEligible: boolean("warehouse_eligible").default(false),
  wholesaleGovernorate: text("wholesale_governorate"),
  wholesaleArea: text("wholesale_area"),
  piecesPerUnit: integer("pieces_per_unit"),
  // Stock tracking (for merchant listings — set to null for unlimited)
  quantity: integer("quantity"),
  // Dropshipping fields
  dropshippingAvailable: boolean("dropshipping_available").default(false),
  isFridayMarket: boolean("is_friday_market").default(false),
  scheduledForFriday: boolean("scheduled_for_friday").default(false),
  scheduledPublishAt: timestamp("scheduled_publish_at"),
  dropshippingPrice: bigint("dropshipping_price", { mode: "number" }),
  // Wholesale market classification
  wholesaleMarket: text("wholesale_market"),
  // Baghdad market the ad is offered in (issue #169, shared/baghdadMarkets.ts).
  // No default and no backfill: NULL on older ads, which show in their
  // seller's account market (scripts/migrations/2026-10-listing-baghdad-market.sql).
  baghdadMarket: text("baghdad_market"),
  // Full-Text Search — عمود مُولَّد تلقائياً يجمع العنوان والوصف بالعربية
  searchVector: (tsvector("search_vector") as any).generatedAlwaysAs(
    sql`to_tsvector('arabic', coalesce(title,'') || ' ' || coalesce(description,''))`,
    { mode: "stored" },
  ),
}, (table) => [
  index("listings_user_id_idx").on(table.userId),
  index("listings_category_idx").on(table.category),
  index("listings_is_active_idx").on(table.isActive),
  index("listings_created_at_idx").on(table.createdAt),
  index("listings_city_idx").on(table.city),
  index("listings_is_premium_idx").on(table.isPremium),
  // فهرس السعر — لتصفية النطاق السعري (price BETWEEN ? AND ?)
  index("listings_price_idx").on(table.price),
  // فهارس مركّبة للاستعلامات الشائعة
  // "سيارات في بغداد" → category + city
  index("listings_cat_city_idx").on(table.category, table.city),
  // الصفحة الرئيسية وصفحات التصنيف → isActive + createdAt (newest first)
  index("listings_active_created_idx").on(table.isActive, table.createdAt),
  // تصفح تصنيف معيّن مع فلتر الحالة
  index("listings_cat_active_idx").on(table.category, table.isActive),
  // فهرس GIN للبحث النصي الكامل — الأسرع لاستعلامات @@ tsvector
  index("listings_search_vector_gin").using("gin", table.searchVector),
]);

// Feature Requests Table (for paid featured items - listings, stores, products)
export const featureRequests = pgTable("feature_requests", {
  id: serial("id").primaryKey(),
  listingId: integer("listing_id"),
  userId: text("user_id").notNull(),
  targetType: text("target_type").notNull().default("listing"),
  targetId: text("target_id").notNull().default("0"),
  targetLabel: text("target_label"),
  requestedDays: integer("requested_days").notNull().default(7),
  amount: integer("amount").notNull(),
  status: text("status").notNull().default("pending"),
  adminNote: text("admin_note"),
  createdAt: timestamp("created_at").defaultNow(),
  processedAt: timestamp("processed_at"),
  processedBy: text("processed_by"),
});

export const FEATURE_TARGET_TYPES = {
  listing: { label: "إعلان", labelPlural: "إعلانات" },
  retail_store: { label: "محل مفرد", labelPlural: "محلات مفرد" },
  wholesale_store: { label: "متجر جملة", labelPlural: "متاجر جملة" },
  virtual_store: { label: "متجر افتراضي", labelPlural: "متاجر افتراضية" },
  product: { label: "منتج", labelPlural: "منتجات" },
} as const;

export type FeatureTargetType = keyof typeof FEATURE_TARGET_TYPES;

export const insertFeatureRequestSchema = createInsertSchema(featureRequests).omit({
  id: true,
  status: true,
  adminNote: true,
  createdAt: true,
  processedAt: true,
  processedBy: true,
});

export type InsertFeatureRequest = z.infer<typeof insertFeatureRequestSchema>;
export type FeatureRequest = typeof featureRequests.$inferSelect;

export const insertListingSchema = createInsertSchema(listings)
  .omit({
    id: true,
    listingCode: true,
    createdAt: true,
    updatedAt: true,
    views: true,
    searchVector: true as any,
  })
  .extend({
    // Arabic error messages for required text fields
    title: z.string({ required_error: "عنوان الإعلان مطلوب" })
      .min(5, "يجب أن يكون العنوان 5 أحرف على الأقل")
      .max(100, "العنوان طويل جداً، الحد الأقصى 100 حرف"),
    category: z.string({ required_error: "القسم مطلوب" })
      .min(1, "يرجى اختيار القسم"),
    location: z.string({ required_error: "الموقع مطلوب" })
      .min(1, "يرجى إدخال الموقع"),
    city: z.string({ required_error: "المدينة مطلوبة" })
      .min(1, "يرجى اختيار المدينة").optional(),
    description: z.string({ required_error: "وصف الإعلان مطلوب" })
      .min(10, "يجب أن يكون الوصف 10 أحرف على الأقل")
      .max(10000, "الوصف طويل جداً"),
    // Price must be a positive integer — drizzle-zod alone allows 0
    price: positivePriceSchema,
    // Secondary money/quantity fields — drizzle-zod alone would accept negative
    // and fractional values, so bound them explicitly. All are optional/nullable.
    wholesalePrice: z.number().int().nonnegative("سعر الجملة لا يمكن أن يكون سالباً").nullable().optional(),
    dropshippingPrice: z.number().int().nonnegative("سعر الدروبشيبينغ لا يمكن أن يكون سالباً").nullable().optional(),
    moq: z.number().int().positive("الحد الأدنى للطلب يجب أن يكون أكبر من صفر").nullable().optional(),
    quantity: z.number().int().nonnegative("الكمية لا يمكن أن تكون سالبة").nullable().optional(),
    piecesPerUnit: z.number().int().positive().nullable().optional(),
    leadTimeHours: z.number().int().nonnegative().nullable().optional(),
    tierPrices: z.array(z.object({
      minQty: z.number().int().positive(),
      price: z.number().int().positive(),
    })).nullable().optional(),
    // contactPhone is optional but must be Iraqi format if provided
    contactPhone: iraqiPhoneOptionalSchema,
    // Only the two real conditions (or none); whether one is REQUIRED depends
    // on the category and is enforced by the listings routes.
    condition: z.enum(PRODUCT_CONDITIONS, { errorMap: () => ({ message: "حالة المنتج يجب أن تكون «جديد» أو «مستعمل»" }) }).nullable().optional(),
  });

export const updateListingSchema = insertListingSchema.partial().extend({
  // On update: price is optional but must still be positive if present
  price: positivePriceSchema.optional(),
  contactPhone: iraqiPhoneOptionalSchema,
});

export type InsertListing = z.infer<typeof insertListingSchema>;
export type UpdateListingRequest = z.infer<typeof updateListingSchema>;
export type Listing = typeof listings.$inferSelect;

// Favorites Table
// FK constraints (fk_favorites_user, fk_favorites_listing) are managed directly in DB — not via Drizzle to avoid migration conflicts with production data.
export const favorites = pgTable("favorites", {
  id: serial("id").primaryKey(),
  userId: text("user_id").notNull(),
  listingId: integer("listing_id").notNull(),
  createdAt: timestamp("created_at").defaultNow(),
}, (table) => [
  index("favorites_user_id_idx").on(table.userId),
]);

export const insertFavoriteSchema = createInsertSchema(favorites).omit({
  id: true,
  createdAt: true,
});

export type InsertFavorite = z.infer<typeof insertFavoriteSchema>;
export type Favorite = typeof favorites.$inferSelect;

// Reseller Inventory Table - for dropshipping products added by virtual store owners
export const resellerInventory = pgTable("reseller_inventory", {
  id: serial("id").primaryKey(),
  resellerId: text("reseller_id").notNull(),
  originalListingId: integer("original_listing_id").notNull(),
  wholesalerId: text("wholesaler_id").notNull(),
  resalePrice: bigint("resale_price", { mode: "number" }).notNull(),
  quantity: integer("quantity").default(1).notNull(),
  isActive: boolean("is_active").default(true),
  isFeatured: boolean("is_featured").default(false),
  featuredUntil: timestamp("featured_until"),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

export const insertResellerInventorySchema = createInsertSchema(resellerInventory).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export type InsertResellerInventory = z.infer<typeof insertResellerInventorySchema>;
export type ResellerInventory = typeof resellerInventory.$inferSelect;

// Merchant Notifications Table - for notifying merchants about reseller activities
export const merchantNotifications = pgTable("merchant_notifications", {
  id: serial("id").primaryKey(),
  merchantId: text("merchant_id").notNull(), // The wholesaler/merchant who gets notified
  resellerId: text("reseller_id").notNull(), // The reseller who took action
  listingId: integer("listing_id").notNull(), // The listing that was selected
  type: text("type").notNull(), // "product_selected", "order_received", etc.
  title: text("title").notNull(),
  message: text("message").notNull(),
  isRead: boolean("is_read").default(false),
  createdAt: timestamp("created_at").defaultNow(),
});

export const insertMerchantNotificationSchema = createInsertSchema(merchantNotifications).omit({
  id: true,
  createdAt: true,
});

export type InsertMerchantNotification = z.infer<typeof insertMerchantNotificationSchema>;
export type MerchantNotification = typeof merchantNotifications.$inferSelect;

// Dropshipping Orders Table - Orders placed by buyers through virtual shops
export const dropshippingOrders = pgTable("dropshipping_orders", {
  id: serial("id").primaryKey(),
  orderCode: text("order_code"),
  // The buyer who placed the order
  buyerId: text("buyer_id"), // Can be null for guest buyers
  buyerName: text("buyer_name").notNull(),
  buyerPhone: text("buyer_phone").notNull(),
  buyerAddress: text("buyer_address").notNull(),
  buyerCity: text("buyer_city").notNull(),
  // The virtual shop owner who will receive first notification
  virtualShopOwnerId: text("virtual_shop_owner_id").notNull(),
  virtualShopName: text("virtual_shop_name").notNull(),
  // The original merchant/wholesaler who has the product
  merchantId: text("merchant_id").notNull(),
  merchantName: text("merchant_name"),
  // Product details
  listingId: integer("listing_id").notNull(),
  productTitle: text("product_title").notNull(),
  productImage: text("product_image"),
  quantity: integer("quantity").default(1).notNull(),
  unitPrice: bigint("unit_price", { mode: "number" }).notNull(), // Sale price to buyer (resale price)
  totalPrice: bigint("total_price", { mode: "number" }).notNull(), // Total paid by buyer
  costPrice: bigint("cost_price", { mode: "number" }), // Original merchant price (for profit calculation)
  shopProfit: bigint("shop_profit", { mode: "number" }), // Shop owner's profit (totalPrice - cost - fees)
  merchantAmount: bigint("merchant_amount", { mode: "number" }), // Amount due to merchant
  platformFee: bigint("platform_fee", { mode: "number" }), // Admin fee for platform
  isFinanciallySettled: boolean("is_financially_settled").default(false), // True when delivered and profits distributed
  currency: text("currency").default("IQD").notNull(),
  // Order status flow: pending -> accepted_by_shop -> preparing -> shipped -> delivered OR rejected
  status: text("status").default("pending").notNull(),
  // Timestamps
  createdAt: timestamp("created_at").defaultNow(),
  acceptedByShopAt: timestamp("accepted_by_shop_at"),
  acceptedByMerchantAt: timestamp("accepted_by_merchant_at"),
  shippedAt: timestamp("shipped_at"),
  deliveredAt: timestamp("delivered_at"),
  rejectedAt: timestamp("rejected_at"),
  rejectionReason: text("rejection_reason"),
  // Notes
  buyerNotes: text("buyer_notes"),
  shopNotes: text("shop_notes"),
  merchantNotes: text("merchant_notes"),
  // Delivery confirmation
  deliveryToken: text("delivery_token"),
  deliveryOtp: text("delivery_otp"),
  deliveryFailedNote: text("delivery_failed_note"),
  deliveryFailedAt: timestamp("delivery_failed_at"),
}, (table) => [
  index("dropshipping_orders_buyer_id_idx").on(table.buyerId),
  index("dropshipping_orders_shop_owner_idx").on(table.virtualShopOwnerId),
  index("dropshipping_orders_merchant_id_idx").on(table.merchantId),
]);

export const insertDropshippingOrderSchema = createInsertSchema(dropshippingOrders).omit({
  id: true,
  createdAt: true,
  acceptedByShopAt: true,
  acceptedByMerchantAt: true,
  shippedAt: true,
  deliveredAt: true,
  rejectedAt: true,
});

export type InsertDropshippingOrder = z.infer<typeof insertDropshippingOrderSchema>;
export type DropshippingOrder = typeof dropshippingOrders.$inferSelect;

// Virtual Shop Notifications Table - for notifying virtual shop owners about incoming orders
export const virtualShopNotifications = pgTable("virtual_shop_notifications", {
  id: serial("id").primaryKey(),
  shopOwnerId: text("shop_owner_id").notNull(), // The virtual shop owner who gets notified
  // The related order. NULL for notices not tied to an order (e.g. the
  // own-product condition campaign) — never a fake id.
  orderId: integer("order_id"),
  type: text("type").notNull(), // "new_order", "order_delivered", etc.
  title: text("title").notNull(),
  message: text("message").notNull(),
  isRead: boolean("is_read").default(false),
  createdAt: timestamp("created_at").defaultNow(),
});

export const insertVirtualShopNotificationSchema = createInsertSchema(virtualShopNotifications).omit({
  id: true,
  createdAt: true,
});

export type InsertVirtualShopNotification = z.infer<typeof insertVirtualShopNotificationSchema>;
export type VirtualShopNotification = typeof virtualShopNotifications.$inferSelect;

// Buyer Notifications Table - for notifying buyers about their order status
export const buyerNotifications = pgTable("buyer_notifications", {
  id: serial("id").primaryKey(),
  buyerId: text("buyer_id"), // Can be null for guest buyers
  buyerPhone: text("buyer_phone").notNull(), // Phone is always required for order tracking
  orderId: integer("order_id").notNull(), // The dropshipping order
  type: text("type").notNull(), // "order_accepted", "order_preparing", "order_shipped", "order_delivered"
  title: text("title").notNull(),
  message: text("message").notNull(),
  isRead: boolean("is_read").default(false),
  createdAt: timestamp("created_at").defaultNow(),
});

export const insertBuyerNotificationSchema = createInsertSchema(buyerNotifications).omit({
  id: true,
  createdAt: true,
});

export type InsertBuyerNotification = z.infer<typeof insertBuyerNotificationSchema>;
export type BuyerNotification = typeof buyerNotifications.$inferSelect;

// Conversations Table
// FK constraints (fk_conversations_buyer, fk_conversations_seller) are managed directly in DB — not via Drizzle to avoid migration conflicts with production data.
export const conversations = pgTable("conversations", {
  id: serial("id").primaryKey(),
  listingId: integer("listing_id").notNull(),
  buyerId: text("buyer_id").notNull(),
  sellerId: text("seller_id").notNull(),
  lastMessageAt: timestamp("last_message_at").defaultNow(),
  adminArchiveReadAt: timestamp("admin_archive_read_at"),
  createdAt: timestamp("created_at").defaultNow(),
}, (table) => [
  index("conversations_buyer_id_idx").on(table.buyerId),
  index("conversations_seller_id_idx").on(table.sellerId),
]);

export const insertConversationSchema = createInsertSchema(conversations).omit({
  id: true,
  lastMessageAt: true,
  createdAt: true,
});

export type InsertConversation = z.infer<typeof insertConversationSchema>;
export type Conversation = typeof conversations.$inferSelect;

// Messages Table
// FK constraints (fk_messages_conversation, fk_messages_sender) are managed directly in DB — not via Drizzle to avoid migration conflicts with production data.
export const messages = pgTable("messages", {
  id: serial("id").primaryKey(),
  conversationId: integer("conversation_id").notNull(),
  senderId: text("sender_id").notNull(),
  content: text("content").notNull(),
  isRead: boolean("is_read").default(false),
  createdAt: timestamp("created_at").defaultNow(),
}, (table) => [
  index("messages_conversation_id_idx").on(table.conversationId),
]);

export const insertMessageSchema = createInsertSchema(messages).omit({
  id: true,
  isRead: true,
  createdAt: true,
});

export type InsertMessage = z.infer<typeof insertMessageSchema>;
export type Message = typeof messages.$inferSelect;

// Site Settings Table (for admin-configurable settings)
export const siteSettings = pgTable("site_settings", {
  id: serial("id").primaryKey(),
  key: text("key").notNull().unique(),
  value: jsonb("value"),
  label: text("label"),
  description: text("description"),
  updatedAt: timestamp("updated_at").defaultNow(),
});

export const insertSiteSettingSchema = createInsertSchema(siteSettings).omit({
  id: true,
  updatedAt: true,
});

export type InsertSiteSetting = z.infer<typeof insertSiteSettingSchema>;
export type SiteSetting = typeof siteSettings.$inferSelect;

// Inventory Table - Products bought by users for resale
export const inventory = pgTable("inventory", {
  id: serial("id").primaryKey(),
  userId: text("user_id").notNull(),
  originalListingId: integer("original_listing_id").notNull(),
  purchasePrice: bigint("purchase_price", { mode: "number" }).notNull(),
  title: text("title").notNull(),
  description: text("description").notNull(),
  category: text("category").notNull(),
  subcategory: text("subcategory"),
  condition: text("condition"),
  imageUrls: text("image_urls").array().default([]),
  location: text("location"),
  city: text("city"),
  district: text("district"),
  status: text("status").default("stored").notNull(), // stored, listed, sold
  listedPrice: bigint("listed_price", { mode: "number" }),
  quantity: integer("quantity").default(1).notNull(), // Available quantity
  resaleListingId: integer("resale_listing_id"),
  purchasedAt: timestamp("purchased_at").defaultNow(),
  listedAt: timestamp("listed_at"),
  soldAt: timestamp("sold_at"),
});

export const insertInventorySchema = createInsertSchema(inventory).omit({
  id: true,
  status: true,
  resaleListingId: true,
  purchasedAt: true,
  listedAt: true,
  soldAt: true,
});

export type InsertInventory = z.infer<typeof insertInventorySchema>;
export type Inventory = typeof inventory.$inferSelect;

// Orders Table - For checkout and payment
export const orders = pgTable("orders", {
  id: serial("id").primaryKey(),
  orderCode: text("order_code"),
  userId: text("user_id").notNull(),
  status: text("status").default("pending").notNull(), // pending, confirmed, processing, shipped, delivered, cancelled
  paymentMethod: text("payment_method").notNull(), // cash_on_delivery, bank_transfer, zain_cash, asia_hawala
  paymentStatus: text("payment_status").default("pending").notNull(), // pending, paid, failed
  totalAmount: bigint("total_amount", { mode: "number" }).notNull(),
  currency: text("currency").default("IQD").notNull(),
  shippingName: text("shipping_name").notNull(),
  shippingPhone: text("shipping_phone").notNull(),
  shippingCity: text("shipping_city").notNull(),
  shippingAddress: text("shipping_address").notNull(),
  notes: text("notes"),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
  confirmedAt: timestamp("confirmed_at"),
  processingAt: timestamp("processing_at"),
  shippedAt: timestamp("shipped_at"),
  deliveredAt: timestamp("delivered_at"),
  cancelledAt: timestamp("cancelled_at"),
  deliveryToken: text("delivery_token"),
  deliveryOtp: text("delivery_otp"),
  deliveryFailedNote: text("delivery_failed_note"),
  deliveryFailedAt: timestamp("delivery_failed_at"),
  stripePaymentIntentId: text("stripe_payment_intent_id"),
  stripeClientSecret: text("stripe_client_secret"),
  paidAt: timestamp("paid_at"),
}, (table) => [
  index("orders_user_id_idx").on(table.userId),
  index("orders_status_idx").on(table.status),
  index("orders_payment_intent_idx").on(table.stripePaymentIntentId),
]);

export const insertOrderSchema = createInsertSchema(orders).omit({
  id: true,
  status: true,
  paymentStatus: true,
  createdAt: true,
  updatedAt: true,
  confirmedAt: true,
  processingAt: true,
  shippedAt: true,
  deliveredAt: true,
  cancelledAt: true,
  deliveryToken: true,
  stripePaymentIntentId: true,
  stripeClientSecret: true,
  paidAt: true,
});

export type InsertOrder = z.infer<typeof insertOrderSchema>;
export type Order = typeof orders.$inferSelect;

// Order Items Table - Individual items in an order
export const orderItems = pgTable("order_items", {
  id: serial("id").primaryKey(),
  orderId: integer("order_id").notNull(),
  listingId: integer("listing_id").notNull(),
  sellerId: text("seller_id").notNull(),
  title: text("title").notNull(),
  price: bigint("price", { mode: "number" }).notNull(),
  quantity: integer("quantity").notNull(),
  currency: text("currency").default("IQD").notNull(),
  imageUrl: text("image_url"),
  /** True if listings.quantity was finite at purchase and stock was deducted for this line (used for idempotent cancel restore). */
  inventoryDeducted: boolean("inventory_deducted").default(false).notNull(),
}, (table) => [
  index("order_items_order_id_idx").on(table.orderId),
  index("order_items_seller_id_idx").on(table.sellerId),
]);

export const insertOrderItemSchema = createInsertSchema(orderItems).omit({
  id: true,
});

export type InsertOrderItem = z.infer<typeof insertOrderItemSchema>;
export type OrderItem = typeof orderItems.$inferSelect;

// Direct Wholesale Orders Table - shop-to-wholesaler orders kept separate from retail orders
export const directWholesaleOrders = pgTable("direct_wholesale_orders", {
  id: serial("id").primaryKey(),
  orderCode: text("order_code"),
  shopOwnerId: text("shop_owner_id").notNull(),
  shopName: text("shop_name"),
  wholesalerId: text("wholesaler_id").notNull(),
  wholesalerName: text("wholesaler_name"),
  status: text("status").default("pending").notNull(), // pending, confirmed, processing, shipped, delivered, cancelled
  paymentMethod: text("payment_method").notNull(),
  paymentStatus: text("payment_status").default("pending").notNull(),
  totalAmount: bigint("total_amount", { mode: "number" }).notNull(),
  currency: text("currency").default("IQD").notNull(),
  shippingName: text("shipping_name").notNull(),
  shippingPhone: text("shipping_phone").notNull(),
  shippingCity: text("shipping_city").notNull(),
  shippingAddress: text("shipping_address").notNull(),
  notes: text("notes"),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
  confirmedAt: timestamp("confirmed_at"),
  processingAt: timestamp("processing_at"),
  shippedAt: timestamp("shipped_at"),
  deliveredAt: timestamp("delivered_at"),
  cancelledAt: timestamp("cancelled_at"),
  rejectionReason: text("rejection_reason"),
  deliveryToken: text("delivery_token"),
  deliveryOtp: text("delivery_otp"),
  deliveryFailedNote: text("delivery_failed_note"),
  deliveryFailedAt: timestamp("delivery_failed_at"),
});

export const insertDirectWholesaleOrderSchema = createInsertSchema(directWholesaleOrders).omit({
  id: true,
  status: true,
  paymentStatus: true,
  createdAt: true,
  updatedAt: true,
  confirmedAt: true,
  processingAt: true,
  shippedAt: true,
  deliveredAt: true,
  cancelledAt: true,
  rejectionReason: true,
  deliveryToken: true,
  deliveryOtp: true,
  deliveryFailedNote: true,
  deliveryFailedAt: true,
});

export type InsertDirectWholesaleOrder = z.infer<typeof insertDirectWholesaleOrderSchema>;
export type DirectWholesaleOrder = typeof directWholesaleOrders.$inferSelect;

// Direct Wholesale Order Items - individual lines in a shop-to-wholesaler order
export const directWholesaleOrderItems = pgTable("direct_wholesale_order_items", {
  id: serial("id").primaryKey(),
  orderId: integer("order_id").notNull(),
  listingId: integer("listing_id").notNull(),
  wholesalerId: text("wholesaler_id").notNull(),
  title: text("title").notNull(),
  price: bigint("price", { mode: "number" }).notNull(),
  quantity: integer("quantity").notNull(),
  moqAtPurchase: integer("moq_at_purchase"),
  currency: text("currency").default("IQD").notNull(),
  imageUrl: text("image_url"),
});

export const insertDirectWholesaleOrderItemSchema = createInsertSchema(directWholesaleOrderItems).omit({
  id: true,
});

export type InsertDirectWholesaleOrderItem = z.infer<typeof insertDirectWholesaleOrderItemSchema>;
export type DirectWholesaleOrderItem = typeof directWholesaleOrderItems.$inferSelect;

// Contact Messages Table - Simple one-way messages to admin
export const contactMessages = pgTable("contact_messages", {
  id: serial("id").primaryKey(),
  userId: text("user_id"),
  name: text("name").notNull(),
  phone: text("phone"),
  email: text("email"),
  subject: text("subject").notNull(),
  message: text("message").notNull(),
  status: text("status").default("new").notNull(),
  createdAt: timestamp("created_at").defaultNow(),
  readAt: timestamp("read_at"),
});

export const insertContactMessageSchema = createInsertSchema(contactMessages).omit({
  id: true,
  status: true,
  createdAt: true,
  readAt: true,
});

export type InsertContactMessage = z.infer<typeof insertContactMessageSchema>;
export type ContactMessage = typeof contactMessages.$inferSelect;

// Admin Conversations Table - Chat threads between users and admin
export const adminConversations = pgTable("admin_conversations", {
  id: serial("id").primaryKey(),
  userId: text("user_id").notNull(),
  userName: text("user_name").notNull(),
  userPhone: text("user_phone"),
  userType: text("user_type"),
  status: text("status").default("open").notNull(),
  lastMessageAt: timestamp("last_message_at").defaultNow(),
  unreadUserCount: integer("unread_user_count").default(0).notNull(),
  unreadAdminCount: integer("unread_admin_count").default(0).notNull(),
  createdAt: timestamp("created_at").defaultNow(),
});

export const insertAdminConversationSchema = createInsertSchema(adminConversations).omit({
  id: true,
  status: true,
  lastMessageAt: true,
  unreadUserCount: true,
  unreadAdminCount: true,
  createdAt: true,
});

export type InsertAdminConversation = z.infer<typeof insertAdminConversationSchema>;
export type AdminConversation = typeof adminConversations.$inferSelect;

// Admin Messages Table - Messages within admin conversations
export const adminMessages = pgTable("admin_messages", {
  id: serial("id").primaryKey(),
  conversationId: integer("conversation_id").notNull(),
  senderId: text("sender_id").notNull(),
  senderType: text("sender_type").notNull(),
  content: text("content").notNull(),
  isRead: boolean("is_read").default(false).notNull(),
  createdAt: timestamp("created_at").defaultNow(),
});

export const insertAdminMessageSchema = createInsertSchema(adminMessages).omit({
  id: true,
  isRead: true,
  createdAt: true,
});

export type InsertAdminMessage = z.infer<typeof insertAdminMessageSchema>;
export type AdminMessage = typeof adminMessages.$inferSelect;

// Price Alerts Table - Notify users when price drops
export const priceAlerts = pgTable("price_alerts", {
  id: serial("id").primaryKey(),
  userId: text("user_id").notNull(),
  listingId: integer("listing_id").notNull(),
  targetPrice: bigint("target_price", { mode: "number" }).notNull(),
  originalPrice: bigint("original_price", { mode: "number" }).notNull(),
  currency: text("currency").default("IQD").notNull(),
  isActive: boolean("is_active").default(true).notNull(),
  isTriggered: boolean("is_triggered").default(false).notNull(),
  triggeredAt: timestamp("triggered_at"),
  createdAt: timestamp("created_at").defaultNow(),
});

export const insertPriceAlertSchema = createInsertSchema(priceAlerts).omit({
  id: true,
  isActive: true,
  isTriggered: true,
  triggeredAt: true,
  createdAt: true,
});

export type InsertPriceAlert = z.infer<typeof insertPriceAlertSchema>;
export type PriceAlert = typeof priceAlerts.$inferSelect;

// Search Alerts Table - Notify users of new listings matching saved searches
export const searchAlerts = pgTable("search_alerts", {
  id: serial("id").primaryKey(),
  userId: text("user_id").notNull(),
  name: text("name").notNull(),
  category: text("category"),
  subcategory: text("subcategory"),
  city: text("city"),
  minPrice: bigint("min_price", { mode: "number" }),
  maxPrice: bigint("max_price", { mode: "number" }),
  currency: text("currency").default("IQD"),
  keywords: text("keywords"),
  isActive: boolean("is_active").default(true).notNull(),
  lastCheckedAt: timestamp("last_checked_at"),
  createdAt: timestamp("created_at").defaultNow(),
});

export const insertSearchAlertSchema = createInsertSchema(searchAlerts).omit({
  id: true,
  isActive: true,
  lastCheckedAt: true,
  createdAt: true,
});

export type InsertSearchAlert = z.infer<typeof insertSearchAlertSchema>;
export type SearchAlert = typeof searchAlerts.$inferSelect;

// Reviews Table - Seller ratings and reviews
export const reviews = pgTable("reviews", {
  id: serial("id").primaryKey(),
  reviewerId: text("reviewer_id").notNull(),
  sellerId: text("seller_id").notNull(),
  listingId: integer("listing_id"),
  orderId: integer("order_id"),
  rating: integer("rating").notNull(), // 1-5 stars
  comment: text("comment"),
  isVerifiedPurchase: boolean("is_verified_purchase").default(false).notNull(),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

export const insertReviewSchema = createInsertSchema(reviews).omit({
  id: true,
  isVerifiedPurchase: true,
  createdAt: true,
  updatedAt: true,
});

export type InsertReview = z.infer<typeof insertReviewSchema>;
export type Review = typeof reviews.$inferSelect;

// Store Reviews Table - Ratings for virtual stores (response speed, service quality)
export const storeReviews = pgTable("store_reviews", {
  id: serial("id").primaryKey(),
  reviewerId: text("reviewer_id").notNull(),         // buyer who placed the order
  shopOwnerId: text("shop_owner_id").notNull(),       // virtual shop owner being rated
  orderId: integer("order_id"),                       // dropshipping order that triggered the review
  responseSpeedRating: integer("response_speed_rating").notNull(), // 1-5: how fast the shop confirmed
  overallRating: integer("overall_rating").notNull(),              // 1-5: overall experience
  comment: text("comment"),
  isVerifiedPurchase: boolean("is_verified_purchase").default(true).notNull(),
  createdAt: timestamp("created_at").defaultNow(),
});

export const insertStoreReviewSchema = createInsertSchema(storeReviews).omit({
  id: true,
  isVerifiedPurchase: true,
  createdAt: true,
});

export type InsertStoreReview = z.infer<typeof insertStoreReviewSchema>;
export type StoreReview = typeof storeReviews.$inferSelect;

// Recently Viewed Table - Track user's viewed listings
export const recentlyViewed = pgTable("recently_viewed", {
  id: serial("id").primaryKey(),
  userId: text("user_id").notNull(),
  listingId: integer("listing_id").notNull(),
  viewedAt: timestamp("viewed_at").defaultNow(),
});

export const insertRecentlyViewedSchema = createInsertSchema(recentlyViewed).omit({
  id: true,
  viewedAt: true,
});

export type InsertRecentlyViewed = z.infer<typeof insertRecentlyViewedSchema>;
export type RecentlyViewed = typeof recentlyViewed.$inferSelect;

// Compare Lists Table - Store user's comparison lists
export const compareLists = pgTable("compare_lists", {
  id: serial("id").primaryKey(),
  visitorId: text("visitor_id").notNull(), // Can be anonymous or user ID
  listingIds: jsonb("listing_ids").notNull().$type<number[]>(),
  category: text("category"),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (table) => [
  index("compare_lists_visitor_id_idx").on(table.visitorId),
])

export const insertCompareListSchema = createInsertSchema(compareLists).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export type InsertCompareList = z.infer<typeof insertCompareListSchema>;
export type CompareList = typeof compareLists.$inferSelect;

// Listing Reports Table - For reporting listings to admin
export const listingReports = pgTable("listing_reports", {
  id: serial("id").primaryKey(),
  listingId: integer("listing_id").notNull(),
  reporterId: text("reporter_id"),
  reporterName: text("reporter_name").notNull(),
  reporterPhone: text("reporter_phone"),
  reason: text("reason").notNull(),
  description: text("description").notNull(),
  listingUrl: text("listing_url").notNull(),
  status: text("status").default("pending").notNull(), // pending, reviewed, resolved, dismissed
  adminNotes: text("admin_notes"),
  createdAt: timestamp("created_at").defaultNow(),
  resolvedAt: timestamp("resolved_at"),
});

export const insertListingReportSchema = createInsertSchema(listingReports).omit({
  id: true,
  status: true,
  adminNotes: true,
  createdAt: true,
  resolvedAt: true,
});

export type InsertListingReport = z.infer<typeof insertListingReportSchema>;
export type ListingReport = typeof listingReports.$inferSelect;

// === Dynamic Admin-Managed Tables ===

// Dynamic Categories Table - Admin-managed categories
export const dynamicCategories = pgTable("dynamic_categories", {
  id: serial("id").primaryKey(),
  key: text("key").notNull().unique(), // e.g. "alisveris", "vasita"
  nameAr: text("name_ar").notNull(),
  nameEn: text("name_en"),
  icon: text("icon"), // icon identifier
  isActive: boolean("is_active").default(true).notNull(),
  sortOrder: integer("sort_order").default(0).notNull(),
  createdAt: timestamp("created_at").defaultNow(),
});

export const insertDynamicCategorySchema = createInsertSchema(dynamicCategories).omit({
  id: true,
  createdAt: true,
});

export type InsertDynamicCategory = z.infer<typeof insertDynamicCategorySchema>;
export type DynamicCategory = typeof dynamicCategories.$inferSelect;

// Dynamic Subcategories Table - Admin-managed subcategories (supports nesting via parentId)
export const dynamicSubcategories = pgTable("dynamic_subcategories", {
  id: serial("id").primaryKey(),
  categoryId: integer("category_id").notNull(),
  parentId: integer("parent_id"), // null = top-level, otherwise nested under parent subcategory
  name: text("name").notNull(),
  isActive: boolean("is_active").default(true).notNull(),
  sortOrder: integer("sort_order").default(0).notNull(),
  createdAt: timestamp("created_at").defaultNow(),
});

export const insertDynamicSubcategorySchema = createInsertSchema(dynamicSubcategories).omit({
  id: true,
  createdAt: true,
});

export type InsertDynamicSubcategory = z.infer<typeof insertDynamicSubcategorySchema>;
export type DynamicSubcategory = typeof dynamicSubcategories.$inferSelect;

// Dynamic Locations Table - Admin-managed governorates and cities
export const dynamicLocations = pgTable("dynamic_locations", {
  id: serial("id").primaryKey(),
  governorate: text("governorate").notNull(),
  city: text("city").notNull(),
  isActive: boolean("is_active").default(true).notNull(),
  sortOrder: integer("sort_order").default(0).notNull(),
  createdAt: timestamp("created_at").defaultNow(),
});

export const insertDynamicLocationSchema = createInsertSchema(dynamicLocations).omit({
  id: true,
  createdAt: true,
});

export type InsertDynamicLocation = z.infer<typeof insertDynamicLocationSchema>;
export type DynamicLocation = typeof dynamicLocations.$inferSelect;

// Dynamic Listing Fields Table - Admin-managed custom fields for product creation
export const dynamicListingFields = pgTable("dynamic_listing_fields", {
  id: serial("id").primaryKey(),
  categoryKey: text("category_key"), // null for global fields, or specific category key
  fieldKey: text("field_key").notNull(),
  label: text("label").notNull(),
  fieldType: text("field_type").notNull(), // "text", "number", "select", "checkbox", "textarea"
  options: jsonb("options"), // for select fields: array of option strings
  isRequired: boolean("is_required").default(false).notNull(),
  isActive: boolean("is_active").default(true).notNull(),
  sortOrder: integer("sort_order").default(0).notNull(),
  createdAt: timestamp("created_at").defaultNow(),
});

export const insertDynamicListingFieldSchema = createInsertSchema(dynamicListingFields).omit({
  id: true,
  createdAt: true,
});

export type InsertDynamicListingField = z.infer<typeof insertDynamicListingFieldSchema>;
export type DynamicListingField = typeof dynamicListingFields.$inferSelect;

// ==========================================
// FINANCIAL SYSTEM - Wallets & Transactions
// ==========================================

// Wallets Table - Internal electronic wallets for shop owners and merchants
export const wallets = pgTable("wallets", {
  id: serial("id").primaryKey(),
  userId: text("user_id").notNull(), // Links to user
  role: text("role").notNull(), // "virtual_shop_owner", "merchant", "platform"
  balance: bigint("balance", { mode: "number" }).default(0).notNull(), // Available (withdrawable) balance
  pendingBalance: bigint("pending_balance", { mode: "number" }).default(0).notNull(), // Earnings locked in shipped/accepted orders — released on delivery
  lockedBalance: bigint("locked_balance", { mode: "number" }).default(0).notNull(), // Amount locked for pending payout requests
  currency: text("currency").default("IQD").notNull(),
  status: text("status").default("active").notNull(), // "active", "suspended"
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (table) => [
  index("wallets_user_id_idx").on(table.userId),
]);

export const insertWalletSchema = createInsertSchema(wallets).omit({
  id: true,
  balance: true,
  pendingBalance: true,
  lockedBalance: true,
  createdAt: true,
  updatedAt: true,
});

export type InsertWallet = z.infer<typeof insertWalletSchema>;
export type Wallet = typeof wallets.$inferSelect;

// Wallet Transactions Table - Immutable ledger for all financial operations
// IMPORTANT: This table should NEVER allow UPDATE or DELETE operations
export const walletTransactions = pgTable("wallet_transactions", {
  id: serial("id").primaryKey(),
  walletId: integer("wallet_id").notNull(), // Links to wallet
  orderId: integer("order_id"), // Links to dropshipping order (optional)
  type: text("type").notNull(), // "credit", "debit", "fee", "payout_hold", "payout_release", "payout_complete"
  amount: bigint("amount", { mode: "number" }).notNull(), // Always positive, type determines direction
  currency: text("currency").default("IQD").notNull(),
  description: text("description").notNull(), // Arabic description of transaction
  reference: jsonb("reference"), // Additional metadata (method, destination, etc.)
  eventKey: text("event_key"), // For idempotency
  actorId: text("actor_id"), // Who initiated the transaction (user or system)
  createdAt: timestamp("created_at").defaultNow(),
}, (table) => [
  index("wallet_tx_wallet_id_idx").on(table.walletId),
  index("wallet_tx_order_id_idx").on(table.orderId),
  // Idempotency: a financial event is recorded at most once. Nullable
  // eventKeys are exempt (Postgres treats NULLs as distinct), so only
  // keyed (idempotent) transactions are constrained to be unique.
  uniqueIndex("wallet_tx_event_key_unique").on(table.eventKey),
]);

export const insertWalletTransactionSchema = createInsertSchema(walletTransactions).omit({
  id: true,
  createdAt: true,
});

export type InsertWalletTransaction = z.infer<typeof insertWalletTransactionSchema>;
export type WalletTransaction = typeof walletTransactions.$inferSelect;

// Payout Requests Table - Withdrawal requests from users
export const payoutRequests = pgTable("payout_requests", {
  id: serial("id").primaryKey(),
  userId: text("user_id").notNull(), // Links to user
  walletId: integer("wallet_id").notNull(), // Links to wallet
  amount: bigint("amount", { mode: "number" }).notNull(), // Amount requested for withdrawal
  currency: text("currency").default("IQD").notNull(),
  method: text("method").notNull(), // "zain_cash", "transfer", "cash"
  destinationDetails: jsonb("destination_details"), // Phone number, bank details, etc.
  status: text("status").default("pending").notNull(), // "pending", "approved", "rejected", "paid"
  adminNotes: text("admin_notes"),
  rejectionReason: text("rejection_reason"),
  createdAt: timestamp("created_at").defaultNow(),
  processedAt: timestamp("processed_at"), // When admin approved/rejected
  paidAt: timestamp("paid_at"), // When payment was completed
}, (table) => [
  index("payout_requests_user_id_idx").on(table.userId),
  index("payout_requests_wallet_id_idx").on(table.walletId),
  index("payout_requests_status_idx").on(table.status),
]);

export const insertPayoutRequestSchema = createInsertSchema(payoutRequests).omit({
  id: true,
  status: true,
  adminNotes: true,
  rejectionReason: true,
  createdAt: true,
  processedAt: true,
  paidAt: true,
});

export type InsertPayoutRequest = z.infer<typeof insertPayoutRequestSchema>;
export type PayoutRequest = typeof payoutRequests.$inferSelect;

// Password Reset Requests Table
export const passwordResetRequests = pgTable("password_reset_requests", {
  id: serial("id").primaryKey(),
  phone: text("phone").notNull(),
  status: text("status").default("pending").notNull(),
  adminNote: text("admin_note"),
  createdAt: timestamp("created_at").defaultNow(),
  processedAt: timestamp("processed_at"),
});

export const insertPasswordResetRequestSchema = createInsertSchema(passwordResetRequests).omit({
  id: true,
  status: true,
  adminNote: true,
  createdAt: true,
  processedAt: true,
});

export type InsertPasswordResetRequest = z.infer<typeof insertPasswordResetRequestSchema>;
export type PasswordResetRequest = typeof passwordResetRequests.$inferSelect;

// Admin Activity Logs Table - Tracks all admin actions
// ===== Dispute System =====
export const disputes = pgTable("disputes", {
  id: serial("id").primaryKey(),
  orderId: integer("order_id"),
  orderType: text("order_type").default("regular"), // "regular" | "dropshipping" | "store_own"
  complainantId: text("complainant_id").notNull(),
  complainantName: text("complainant_name"),
  complainantRole: text("complainant_role").notNull(), // "buyer" | "shop_owner" | "merchant"
  subject: text("subject").notNull(),
  description: text("description").notNull(),
  status: text("status").default("open").notNull(), // "open" | "under_review" | "resolved" | "closed"
  adminNotes: text("admin_notes"),
  resolution: text("resolution"),
  resolvedBy: text("resolved_by"),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
  resolvedAt: timestamp("resolved_at"),
});

export const insertDisputeSchema = createInsertSchema(disputes).omit({
  id: true, createdAt: true, updatedAt: true, resolvedAt: true,
  adminNotes: true, resolution: true, resolvedBy: true,
});
export type Dispute = typeof disputes.$inferSelect;
export type InsertDispute = z.infer<typeof insertDisputeSchema>;

export const adminActivityLogs = pgTable("admin_activity_logs", {
  id: serial("id").primaryKey(),
  adminUsername: text("admin_username").notNull(),
  action: text("action").notNull(), // e.g. "suspend_user", "delete_listing", "approve_payout"
  targetType: text("target_type"), // "user", "listing", "payout", "report", "store"
  targetId: text("target_id"), // The ID of the affected entity
  details: text("details"), // Human-readable Arabic description
  createdAt: timestamp("created_at").defaultNow(),
});

export type AdminActivityLog = typeof adminActivityLogs.$inferSelect;

// ─── Delivery Agents ──────────────────────────────────────────────────────────
export const deliveryAgents = pgTable("delivery_agents", {
  id: serial("id").primaryKey(),
  companyName: text("company_name").notNull(),
  contactName: text("contact_name").notNull(),
  phone: text("phone").notNull().unique(),
  passwordHash: text("password_hash").notNull(),
  cities: text("cities").array().default([]).notNull(), // cities they cover
  active: boolean("active").default(true).notNull(),
  createdAt: timestamp("created_at").defaultNow(),
  lastLoginAt: timestamp("last_login_at"),
});

export const insertDeliveryAgentSchema = createInsertSchema(deliveryAgents).omit({
  id: true, createdAt: true, lastLoginAt: true, passwordHash: true,
}).extend({ password: z.string().min(6) });

export type DeliveryAgent = typeof deliveryAgents.$inferSelect;
export type InsertDeliveryAgent = z.infer<typeof insertDeliveryAgentSchema>;

// ── Order Status Logs Table ──────────────────────────────────────────────────
// Immutable append-only audit trail for every status transition
export const orderStatusLogs = pgTable("order_status_logs", {
  id: serial("id").primaryKey(),
  orderId: integer("order_id").notNull(),
  orderType: text("order_type").notNull().default("dropshipping"), // 'regular' | 'dropshipping'
  fromStatus: text("from_status"),           // null for initial creation
  toStatus: text("to_status").notNull(),
  actorId: text("actor_id"),                 // userId / delivery-agent-id / null for system
  actorName: text("actor_name"),             // display name
  actorRole: text("actor_role"),             // 'buyer'|'shop_owner'|'merchant'|'system'|'admin'|'delivery_agent'
  notes: text("notes"),                      // optional message at this step
  createdAt: timestamp("created_at").defaultNow(),
});

export type OrderStatusLog = typeof orderStatusLogs.$inferSelect;

// ===== Virtual Store Own Products =====
// Products the virtual store owner sells directly (not sourced from wholesalers)
export const virtualStoreOwnProducts = pgTable("virtual_store_own_products", {
  id: serial("id").primaryKey(),
  ownerId: text("owner_id").notNull(),
  title: text("title").notNull(),
  description: text("description"),
  price: bigint("price", { mode: "number" }).notNull(),
  quantity: integer("quantity").default(1).notNull(),
  imageUrls: text("image_urls").array().default([]),
  category: text("category"),
  subcategory: text("subcategory"),
  // Optional location fields. Required only for vasita / emlak categories
  // (enforced in the API layer, not at the DB level, so existing rows remain valid).
  city: text("city"),
  district: text("district"),
  isActive: boolean("is_active").default(true).notNull(),
  // «جديد» / «مستعمل», chosen by the owner (required on create and on any
  // edit or reactivation). NULL only on products created before the
  // 2026-10-19 rule, until the owner picks; never offered as a choice.
  // Added by scripts/migrations/2026-10-product-condition.sql, which must run
  // BEFORE this code is deployed: queries here select every column, and the
  // server refuses to start in production without it
  // (server/lib/productConditionSchema.ts).
  condition: text("condition"),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (table) => [
  check("virtual_store_own_products_condition_check", sql`${table.condition} IS NULL OR ${table.condition} IN ('جديد', 'مستعمل')`),
]);

export const insertVirtualStoreOwnProductSchema = createInsertSchema(virtualStoreOwnProducts).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export type InsertVirtualStoreOwnProduct = z.infer<typeof insertVirtualStoreOwnProductSchema>;
export type VirtualStoreOwnProduct = typeof virtualStoreOwnProducts.$inferSelect;

// ===== Product condition campaign (scripts/migrations/2026-10-product-condition.sql) =====
// One row per store owner who received the in-app notice asking them to set
// «جديد / مستعمل» on their legacy own products. UNIQUE(campaign_key, owner_id)
// makes sending idempotent. Rows are written ONLY by the explicit primary-admin
// send action (server/lib/ownProductConditionCampaign.ts) — never by a
// migration, deploy, cron or startup hook.
export const notificationCampaignRecipients = pgTable("notification_campaign_recipients", {
  id: serial("id").primaryKey(),
  campaignKey: text("campaign_key").notNull(),
  ownerId: text("owner_id").notNull(),
  notificationId: integer("notification_id"),
  productCount: integer("product_count").notNull(),
  sentBy: text("sent_by"),
  sentAt: timestamp("sent_at").defaultNow().notNull(),
}, (table) => [
  uniqueIndex("notification_campaign_recipients_campaign_owner_uidx").on(table.campaignKey, table.ownerId),
]);

export type NotificationCampaignRecipient = typeof notificationCampaignRecipients.$inferSelect;

// ===== Store delivery coverage (see shared/storeDelivery.ts) =====
// Kept in their OWN tables on purpose: many queries select every column of
// `users` / `virtual_store_own_products`, so adding columns there would break
// them the moment code shipped ahead of the reviewed migration
// (scripts/migrations/2026-10-store-delivery-coverage.sql). Until both tables
// exist, server/lib/storeDelivery.ts reports the feature as not ready and
// search/orders behave exactly as before.

// Store-level default: the provinces the owner confirmed delivering to.
// NO ROW = not confirmed (the state of every pre-existing store).
export const storeDeliverySettings = pgTable("store_delivery_settings", {
  ownerId: varchar("owner_id").primaryKey().references(() => users.id, { onDelete: "cascade" }),
  provinces: text("provinces").array().notNull(),
  confirmedAt: timestamp("confirmed_at").defaultNow().notNull(),
});

export type StoreDeliverySettings = typeof storeDeliverySettings.$inferSelect;

// Per-product override. NO ROW = inherit the store default.
//   custom → deliver only to `provinces`
//   none   → no delivery for this product (local buyers only)
export const ownProductDeliveryOverrides = pgTable("own_product_delivery_overrides", {
  productId: integer("product_id").primaryKey().references(() => virtualStoreOwnProducts.id, { onDelete: "cascade" }),
  mode: text("mode").notNull(),
  provinces: text("provinces").array().notNull().default(sql`'{}'::text[]`),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
}, (table) => [
  check("own_product_delivery_overrides_mode_check", sql`${table.mode} IN ('custom', 'none')`),
]);

export type OwnProductDeliveryOverrideRow = typeof ownProductDeliveryOverrides.$inferSelect;

// ─── Own-Product Orders ──────────────────────────────────────────────────────
// Orders placed by customers directly on own (physical-store) products
export const virtualStoreOwnProductOrders = pgTable("virtual_store_own_product_orders", {
  id: serial("id").primaryKey(),
  orderCode: text("order_code"),
  // Buyer info
  buyerId: text("buyer_id"),                           // nullable — guest buyers allowed
  buyerName: text("buyer_name").notNull(),
  buyerPhone: text("buyer_phone").notNull(),
  buyerAddress: text("buyer_address").notNull(),
  buyerCity: text("buyer_city").notNull(),
  buyerNotes: text("buyer_notes"),
  // Product / store
  ownProductId: integer("own_product_id").notNull(),
  storeOwnerId: text("store_owner_id").notNull(),
  productTitle: text("product_title").notNull(),
  productImage: text("product_image"),
  quantity: integer("quantity").default(1).notNull(),
  unitPrice: bigint("unit_price", { mode: "number" }).notNull(),
  totalPrice: bigint("total_price", { mode: "number" }).notNull(),
  currency: text("currency").default("IQD").notNull(),
  // Status flow: pending → accepted → shipped → delivered | rejected
  status: text("status").default("pending").notNull(),
  rejectionReason: text("rejection_reason"),
  // B2B flag — buyer is a virtual store owner
  buyerIsVirtualStore: boolean("buyer_is_virtual_store").default(false),
  buyerStoreName: text("buyer_store_name"),
  // Timestamps
  createdAt: timestamp("created_at").defaultNow(),
  acceptedAt: timestamp("accepted_at"),
  shippedAt: timestamp("shipped_at"),
  deliveredAt: timestamp("delivered_at"),
  rejectedAt: timestamp("rejected_at"),
  deliveryToken: text("delivery_token"),
  deliveryOtp: text("delivery_otp"),
  deliveryFailedNote: text("delivery_failed_note"),
  deliveryFailedAt: timestamp("delivery_failed_at"),
});

export const insertVirtualStoreOwnProductOrderSchema = createInsertSchema(virtualStoreOwnProductOrders).omit({
  id: true,
  createdAt: true,
  acceptedAt: true,
  shippedAt: true,
  deliveredAt: true,
  rejectedAt: true,
});

export type InsertVirtualStoreOwnProductOrder = z.infer<typeof insertVirtualStoreOwnProductOrderSchema>;
export type VirtualStoreOwnProductOrder = typeof virtualStoreOwnProductOrders.$inferSelect;

// ===== Returns & Refunds — Phase 1A foundation =====
// Structured return-request domain shared by regular, dropshipping, and store-own
// orders. This phase intentionally adds only the data model: no routes, no UI,
// no money movement. Status terminology mirrors the dispute system to keep the
// admin UX vocabulary consistent. FK constraints are left to the application
// layer (same convention as conversations / messages) so additive deploys work
// without touching existing tables.
export const returnRequests = pgTable("return_requests", {
  id: serial("id").primaryKey(),
  // Human-readable reference shown to buyers (e.g. "RT-2026-000123"). Generated
  // by the application on insert; kept unique at the DB level for safety.
  returnCode: text("return_code").notNull().unique(),
  // Which order family this return targets.
  // 'regular' | 'dropshipping' | 'store_own'
  orderType: text("order_type").notNull(),
  orderId: integer("order_id").notNull(),
  // Regular orders are multi-line; null for whole-order or single-line order types.
  orderItemId: integer("order_item_id"),
  // Snapshot at request time; useful for analytics if the listing is later edited/deleted.
  listingId: integer("listing_id"),
  quantity: integer("quantity").default(1).notNull(),
  // Parties involved on this specific return.
  buyerId: text("buyer_id").notNull(),
  sellerId: text("seller_id").notNull(),
  // Dropshipping only — the virtual shop owner sitting between buyer and merchant.
  shopOwnerId: text("shop_owner_id"),
  // Reason taxonomy: 'damaged' | 'wrong_item' | 'not_as_described' | 'not_received'
  //                | 'defective' | 'missing_parts' | 'changed_mind' | 'other'
  reasonCode: text("reason_code").notNull(),
  reasonText: text("reason_text").notNull(),
  // Evidence images uploaded by the buyer. Stored the same way as other image
  // collections in this codebase (text[] of /uploads/* paths).
  photoUrls: text("photo_urls").array().default([]),
  // Status flow:
  // requested → seller_review → approved → in_transit → received → refunded → closed
  //                          ↘ rejected
  //                          ↘ escalated → admin_review → (approved | rejected | partial)
  // Terminal states: closed | rejected
  status: text("status").default("requested").notNull(),
  // Seller decision metadata
  sellerResponse: text("seller_response"),
  sellerDecidedAt: timestamp("seller_decided_at"),
  sellerDecidedBy: text("seller_decided_by"),
  // Admin override metadata
  // 'approve_full' | 'approve_partial' | 'reject'
  adminDecision: text("admin_decision"),
  adminDecisionNote: text("admin_decision_note"),
  adminDecidedAt: timestamp("admin_decided_at"),
  adminDecidedBy: text("admin_decided_by"),
  // Money — same bigint("...", { mode: "number" }) convention used elsewhere
  // for prices and wallet amounts. Null until a refund is actually issued in
  // a later phase.
  refundAmount: bigint("refund_amount", { mode: "number" }),
  refundCurrency: text("refund_currency").default("IQD"),
  // 'wallet_credit' | 'stripe_refund' | 'cod_cash_handback'
  refundMethod: text("refund_method"),
  // FK-by-convention into walletTransactions.id (managed at app layer).
  refundTxId: integer("refund_tx_id"),
  stripeRefundId: text("stripe_refund_id"),
  // True once stock was put back on the seller's listing for this return.
  restockApplied: boolean("restock_applied").default(false).notNull(),
  // FK-by-convention into disputes.id when escalation creates / links a dispute.
  linkedDisputeId: integer("linked_dispute_id"),
  // Computed at insert time from order.deliveredAt + window setting.
  returnWindowExpiresAt: timestamp("return_window_expires_at").notNull(),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (table) => [
  // Buyer's "my returns" list, newest first.
  index("returns_buyer_created_idx").on(table.buyerId, table.createdAt),
  // Seller inbox filtered by status, newest first.
  index("returns_seller_status_created_idx").on(table.sellerId, table.status, table.createdAt),
  // Lookup by source order (e.g. order detail page → "are there returns on this order?").
  index("returns_order_ref_idx").on(table.orderType, table.orderId),
  // Admin queue grouped by status, newest first.
  index("returns_status_created_idx").on(table.status, table.createdAt),
  // Concurrency guard: at most ONE open return per (orderType, orderId,
  // orderItemId). COALESCE folds a NULL item (whole-order / single-line
  // returns) to a shared key so duplicates collide there too. Terminal
  // states (rejected/closed) are excluded so a new return can be filed after
  // a prior one is resolved.
  uniqueIndex("return_requests_one_open_per_item")
    .on(table.orderType, table.orderId, sql`coalesce(${table.orderItemId}, -1)`)
    .where(sql`status not in ('rejected', 'closed')`),
]);

export const insertReturnRequestSchema = createInsertSchema(returnRequests).omit({
  id: true,
  status: true,
  sellerResponse: true,
  sellerDecidedAt: true,
  sellerDecidedBy: true,
  adminDecision: true,
  adminDecisionNote: true,
  adminDecidedAt: true,
  adminDecidedBy: true,
  refundAmount: true,
  refundMethod: true,
  refundTxId: true,
  stripeRefundId: true,
  restockApplied: true,
  linkedDisputeId: true,
  createdAt: true,
  updatedAt: true,
});

export type InsertReturnRequest = z.infer<typeof insertReturnRequestSchema>;
export type ReturnRequest = typeof returnRequests.$inferSelect;

// Append-only audit trail for return-request status transitions. Mirrors
// orderStatusLogs in shape so the same timeline UI component can render both.
export const returnStatusLogs = pgTable("return_status_logs", {
  id: serial("id").primaryKey(),
  returnId: integer("return_id").notNull(),
  fromStatus: text("from_status"),           // null for initial creation
  toStatus: text("to_status").notNull(),
  actorId: text("actor_id"),                 // userId / admin token / null for system
  actorRole: text("actor_role").notNull(),   // 'buyer'|'seller'|'shop_owner'|'admin'|'system'
  notes: text("notes"),
  createdAt: timestamp("created_at").defaultNow(),
}, (table) => [
  index("return_status_logs_return_id_idx").on(table.returnId, table.createdAt),
]);

export const insertReturnStatusLogSchema = createInsertSchema(returnStatusLogs).omit({
  id: true,
  createdAt: true,
});

export type InsertReturnStatusLog = z.infer<typeof insertReturnStatusLogSchema>;
export type ReturnStatusLog = typeof returnStatusLogs.$inferSelect;

// Native push notification tokens, one row per device per user.
// Populated by the client after the user grants the OS notification
// permission; consumed by the server to deliver locked-screen pushes via
// Firebase Cloud Messaging (Phase 3+). Safe to add ahead of FCM wiring —
// rows accumulate harmlessly until a delivery layer reads them.
export const devicePushTokens = pgTable("device_push_tokens", {
  id: serial("id").primaryKey(),
  // Nullable on purpose. A device registers for broadcasts on first launch,
  // before (or entirely without) ever signing in — those rows carry NULL here.
  // The row is rebound to a real user id by the next authenticated
  // registration on the same device, and reset back to NULL when that account
  // is deleted, so an installed app keeps receiving admin broadcasts either
  // way. Segment audiences (traders / stores / customers) INNER JOIN `users`
  // and therefore skip NULL rows automatically; only the "all" audience
  // reaches them. See server/routes/adminPushCampaigns.ts.
  userId: text("user_id"),
  platform: text("platform").notNull(),       // "ios" | "android" | "web"
  token: text("token").notNull(),             // FCM registration token (or APNs token for web push)
  deviceMetadata: jsonb("device_metadata"),   // optional debug info: model, OS version, app version
  isActive: boolean("is_active").notNull().default(true),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
  lastSeenAt: timestamp("last_seen_at").defaultNow(),
}, (table) => [
  uniqueIndex("device_push_tokens_token_unique").on(table.token),
  index("device_push_tokens_user_active_idx").on(table.userId, table.isActive),
]);

export const insertDevicePushTokenSchema = createInsertSchema(devicePushTokens).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
  lastSeenAt: true,
});

export type InsertDevicePushToken = z.infer<typeof insertDevicePushTokenSchema>;
export type DevicePushToken = typeof devicePushTokens.$inferSelect;

// Admin broadcast campaigns — a log row per mass push the admin sends, kept so
// the team can see when a campaign went out, to whom, and how it landed.
export const pushCampaigns = pgTable("push_campaigns", {
  id: serial("id").primaryKey(),
  title: text("title").notNull(),
  body: text("body").notNull(),
  audience: text("audience").notNull(),       // "all" | "stores" | "customers"
  targetUrl: text("target_url"),              // optional in-app deep link
  recipientCount: integer("recipient_count").notNull().default(0), // devices targeted
  successCount: integer("success_count").notNull().default(0),
  failureCount: integer("failure_count").notNull().default(0),
  createdBy: text("created_by"),              // admin username/actor that sent it
  createdAt: timestamp("created_at").defaultNow(),
}, (table) => [
  index("push_campaigns_created_at_idx").on(table.createdAt),
]);

export const insertPushCampaignSchema = createInsertSchema(pushCampaigns).omit({
  id: true,
  recipientCount: true,
  successCount: true,
  failureCount: true,
  createdAt: true,
});

export type InsertPushCampaign = z.infer<typeof insertPushCampaignSchema>;
export type PushCampaign = typeof pushCampaigns.$inferSelect;

// Unified in-app notification inbox — one row per personal notification so the
// user can re-read what a banner/push showed after it disappeared. Fed by the
// central `sendNotification()` hub in server/ws.ts (fire-and-forget insert), so
// every personal notification (orders, messages, wallet, …) lands here going
// forward. Admin broadcasts are NOT stored here — they live once in
// `push_campaigns` and are merged in at read time by the inbox endpoint. The
// table is created at runtime via CREATE TABLE IF NOT EXISTS
// (server/lib/userNotificationsStore.ts), matching the push_campaigns pattern,
// so no manual Neon migration is needed.
export const userNotifications = pgTable("user_notifications", {
  id: serial("id").primaryKey(),
  userId: text("user_id").notNull(),
  type: text("type"),                 // server notification `type` (for deep-link + icon)
  title: text("title").notNull(),
  body: text("body").notNull(),
  url: text("url"),                   // optional in-app deep link (data.url / targetUrl)
  isRead: boolean("is_read").notNull().default(false),
  createdAt: timestamp("created_at").defaultNow(),
}, (table) => [
  index("user_notifications_user_created_idx").on(table.userId, table.createdAt),
]);

export const insertUserNotificationSchema = createInsertSchema(userNotifications).omit({
  id: true,
  isRead: true,
  createdAt: true,
});

export type InsertUserNotification = z.infer<typeof insertUserNotificationSchema>;
export type UserNotification = typeof userNotifications.$inferSelect;

// ─── DB-managed primitives also created at runtime by the server ─────────────
//
// The following objects already exist in production and are created on demand
// from raw SQL in `server/middleware/adminAuth.ts` and `server/storage.ts`.
// Declaring them here so `drizzle-kit push` recognises them and never tries
// to drop them — they are NOT introduced by this commit, only documented.

// Admin login rate-limiter — IP-keyed counter with reset window.
// Created in adminAuth.ts via CREATE TABLE IF NOT EXISTS.
export const adminFailAttempts = pgTable("admin_fail_attempts", {
  ip: text("ip").primaryKey(),
  count: integer("count").notNull().default(0),
  resetTime: bigint("reset_time", { mode: "number" }).notNull(),
});

// Revoked admin JWT IDs (logout / forced sign-out).
// Created in adminAuth.ts via CREATE TABLE IF NOT EXISTS.
export const adminJwtBlacklist = pgTable("admin_jwt_blacklist", {
  jti: text("jti").primaryKey(),
  expiresAt: bigint("expires_at", { mode: "number" }).notNull(),
});

// Atomic listing-code generator (IQ-1000, IQ-1001, …).
// Created in storage.ts via CREATE SEQUENCE IF NOT EXISTS.
export const listingCodeSeq = pgSequence("listing_code_seq", { startWith: 1000 });
